import type { PlanData, PlanDay } from './types'

export interface ScheduleValidationOptions {
  requestedDaysPerWeek: number
  startDayOfWeek?: number
}

const WEEKDAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

const RECOVERY_TRAINING_DAY = {
  focus: 'Active Recovery Conditioning',
  session_purpose: 'Maintain training frequency with low-impact aerobic work and mobility.',
  is_rest: false,
  warmup: [
    { name: 'Arm Circles', reps_display: '10 ea direction' },
    { name: 'Walking Knee Hugs', reps_display: '10 yds' },
    { name: 'Air Squats', reps_display: '10 reps' },
  ],
  exercises: [
    { category: 'cardio', name: 'Brisk Walk', base_sets: 1, base_reps: 25, reps_unit: 'min', reps_add_per_week: 2, reps_max: 40, rest_seconds: 0, notes: 'conversational pace' },
    { category: 'mobility', name: 'Dead Bug', base_sets: 3, base_reps: 10, reps_unit: 'reps', reps_add_per_week: 1, reps_max: 20, rest_seconds: 30, notes: 'slow controlled reps' },
    { category: 'mobility', name: 'Glute Bridge', base_sets: 3, base_reps: 12, reps_unit: 'reps', reps_add_per_week: 1, reps_max: 25, rest_seconds: 30, notes: 'squeeze at top' },
  ],
  cooldown: [
    { name: 'Hamstring Stretch', hold: '20 sec ea side' },
    { name: 'Hip Flexor Stretch', hold: '20 sec ea side' },
    { name: 'Child Pose', hold: '30 sec' },
  ],
}

export function countTrainingDays(days: PlanDay[]): number {
  return days.filter(day => day.is_rest !== true).length
}

function hasTooManyConsecutiveRestDays(days: PlanDay[]): boolean {
  const ordered = [...days].sort((a, b) => a.day_of_week - b.day_of_week)
  if (ordered.length === 0) return false

  let streak = 0
  for (let i = 0; i < ordered.length * 2; i++) {
    const day = ordered[i % ordered.length]
    if (!day) continue
    if (day.is_rest === true) {
      streak++
      if (streak > 2) return true
    } else {
      streak = 0
    }
  }
  return false
}

function convertRestToTraining(day: PlanDay): void {
  day.focus = day.focus && day.focus !== 'Rest' ? day.focus : RECOVERY_TRAINING_DAY.focus
  day.session_purpose = day.session_purpose || RECOVERY_TRAINING_DAY.session_purpose
  day.is_rest = false
  day.warmup = day.warmup?.length ? day.warmup : RECOVERY_TRAINING_DAY.warmup.map(drill => ({ ...drill }))
  day.exercises = day.exercises?.length ? day.exercises : RECOVERY_TRAINING_DAY.exercises.map(ex => ({ ...ex }))
  day.cooldown = day.cooldown?.length ? day.cooldown : RECOVERY_TRAINING_DAY.cooldown.map(stretch => ({ ...stretch }))
}

function convertTrainingToRest(day: PlanDay): void {
  day.focus = 'Rest'
  day.session_purpose = 'Recovery day to absorb training and prepare for the next session.'
  day.is_rest = true
  day.warmup = []
  day.exercises = []
  day.cooldown = []
}

function orderedDays(days: PlanDay[]): PlanDay[] {
  return [...days].sort((a, b) => a.day_of_week - b.day_of_week)
}

function defaultRestDay(day_of_week: number): PlanDay {
  return {
    day_of_week,
    label: WEEKDAY_LABELS[day_of_week - 1] ?? `Day ${day_of_week}`,
    focus: 'Rest',
    session_purpose: 'Recovery day to absorb training and prepare for the next session.',
    is_rest: true,
    warmup: [],
    exercises: [],
    cooldown: [],
  }
}

/** Pad, trim, or remap LLM output so each phase template is exactly 7 calendar days. */
export function normalizeWeekTemplate(days: PlanDay[] | undefined): PlanDay[] {
  if (!Array.isArray(days) || days.length === 0) {
    return Array.from({ length: 7 }, (_, i) => defaultRestDay(i + 1))
  }

  const byDow = new Map<number, PlanDay>()
  const unassigned: PlanDay[] = []

  for (const day of days) {
    const dow = Number(day.day_of_week)
    if (Number.isFinite(dow) && dow >= 1 && dow <= 7) {
      byDow.set(dow, { ...day, day_of_week: dow })
    } else {
      unassigned.push(day)
    }
  }

  const result: PlanDay[] = []
  for (let dow = 1; dow <= 7; dow++) {
    if (byDow.has(dow)) {
      result.push(byDow.get(dow)!)
      continue
    }
    if (unassigned.length > 0) {
      const next = unassigned.shift()!
      result.push({
        ...next,
        day_of_week: dow,
        label: next.label || (WEEKDAY_LABELS[dow - 1] ?? `Day ${dow}`),
        is_rest: next.is_rest ?? false,
      })
      continue
    }
    result.push(defaultRestDay(dow))
  }

  return result
}

function repairTemplate(days: PlanDay[] | undefined, options: ScheduleValidationOptions): void {
  if (!Array.isArray(days) || days.length !== 7) return

  const ordered = orderedDays(days)
  const startDay = options.startDayOfWeek
    ? ordered.find(day => day.day_of_week === options.startDayOfWeek)
    : null
  if (startDay?.is_rest === true) convertRestToTraining(startDay)

  while (countTrainingDays(ordered) < options.requestedDaysPerWeek) {
    const candidate = ordered.find(day => day.is_rest === true)
    if (!candidate) break
    convertRestToTraining(candidate)
  }

  while (countTrainingDays(ordered) > options.requestedDaysPerWeek) {
    const candidate = [...ordered]
      .reverse()
      .find(day => day.is_rest !== true && day.day_of_week !== options.startDayOfWeek)
    if (!candidate) break
    convertTrainingToRest(candidate)
  }

  let guard = 0
  while (hasTooManyConsecutiveRestDays(ordered) && guard < 7) {
    const candidate = ordered.find(day => day.is_rest === true)
    if (!candidate) break
    convertRestToTraining(candidate)
    guard++
  }
}

export function repairPlanSchedule(plan: PlanData, options: ScheduleValidationOptions): PlanData {
  plan.week_template = normalizeWeekTemplate(plan.week_template)
  repairTemplate(plan.week_template, options)

  if (plan.week_template_phase2 !== undefined) {
    plan.week_template_phase2 = normalizeWeekTemplate(plan.week_template_phase2)
    repairTemplate(plan.week_template_phase2, options)
  }
  return plan
}

function validateTemplate(
  label: string,
  days: PlanDay[] | undefined,
  options: ScheduleValidationOptions,
  errors: string[],
): void {
  if (!Array.isArray(days) || days.length !== 7) {
    errors.push(`${label} must contain exactly 7 days.`)
    return
  }

  const trainingDays = countTrainingDays(days)
  if (trainingDays !== options.requestedDaysPerWeek) {
    errors.push(`${label} has ${trainingDays} training days; expected ${options.requestedDaysPerWeek}.`)
  }

  if (hasTooManyConsecutiveRestDays(days)) {
    errors.push(`${label} has more than two rest days in a row.`)
  }
}

export function validatePlanSchedule(plan: PlanData, options: ScheduleValidationOptions): string[] {
  const errors: string[] = []
  validateTemplate('Phase 1', plan.week_template, options, errors)

  if (Array.isArray(plan.week_template_phase2)) {
    validateTemplate('Phase 2', plan.week_template_phase2, options, errors)
  }

  if (options.startDayOfWeek) {
    const startDay = plan.week_template?.find(day => day.day_of_week === options.startDayOfWeek)
    if (!startDay) {
      errors.push(`Start day ${options.startDayOfWeek} is missing from Phase 1.`)
    } else if (startDay.is_rest === true) {
      errors.push('Plan start date lands on a rest day.')
    }
  }

  return errors
}
